import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppRangePicker from "../../components/Core/AppRangePicker/AppRangePicker";
import AppText from "../../components/Core/AppText/AppText";
import { DownloadButton } from "../../components/Core/Button/Button";
import Dropdown from "../../components/Core/Dropdown/Dropdown";
import Flex from "../../components/Layout/Flex";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import { SoldHoursAndRevenueReportBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { ViewModels } from "../../configs/constants/ViewModels";
import {
	buildContractKeyValues,
	buildCustomerKeyValues,
	buildPoolKeyValues,
	jobStatusTypes,
} from "../../configs/Reports.config";
import { KeyValue } from "../../models/KeyValue";
import { CONTRACT_SORTING_COLUMN } from "../../models/Sorting";
import { useContracts } from "../../networking/contract.hooks";
import { useCustomers } from "../../networking/customer.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { getQueryStringValue } from "../../hooks/useQueryState";
import { apiStore } from "../../stores/api.store";
import { reportStore } from "../../networking/report.api";

const SoldHoursAndRevenueReportPage: React.FC = () => {
	const isDebug = ["true", "1"].includes(getQueryStringValue("debug") || "");

	const { t } = useTranslation();

	const { data: organisation } = useOrganisation();

	const [pool, setPool] = useState<KeyValue>();
	const [range, setRange] = useState<{ start: Date; end: Date }>();

	const [contract, setContract] = useState<KeyValue>();
	const [customer, setCustomer] = useState<KeyValue>();
	const [jobStatus, setJobStatus] = useState<KeyValue>(jobStatusTypes().find((s) => s.key === "")!);
	const [loading, setLoading] = useState<boolean>();
	const [hasErrorInDateRange, setHasErrorInDateRange] = useState<boolean>(false);

	const { contracts = [] } = useContracts({
		view: ViewModels.SELECT_OPTION,
		active: true,
		sort: CONTRACT_SORTING_COLUMN.NAME,
	});
	// Slim projection for the dropdown. The old store call used the default page
	// size (20), silently truncating the options — fetch them all instead.
	const { customers: customersData } = useCustomers({
		page: 1,
		pageSize: 10000,
		view: ViewModels.SELECT_OPTION,
		active: true,
	});

	const pools = buildPoolKeyValues(organisation?.pools || [], t("ALL"));
	const contractOptions = buildContractKeyValues(contracts || [], t("ALL"));
	const customerOptions = buildCustomerKeyValues(customersData?.customers || [], t("ALL"));

	const handlePoolChange = (e: KeyValue) => {
		setPool(e);
	};

	const handleContractChange = (e: KeyValue) => {
		setContract(e);
	};

	const handleCustomerChange = (e: KeyValue) => {
		setCustomer(e);
	};

	const handleJobStatusChange = (e: KeyValue) => {
		setJobStatus(e);
	};

	const onSubmit = async () => {
		let selectedStartDate!: string;
		let selectedEndDate!: string;
		let selectedPool!: string;
		let selectedContract!: string;
		let selectedCustomer!: string;
		let selectedJobStatus!: string;

		if (pool?.key && pool.key !== "") selectedPool = pool.key as string;

		if (range && range.start.toISOString() !== "") selectedStartDate = range.start.toISOString();

		if (range && range.end.toISOString() !== "") selectedEndDate = range.end.toISOString();

		if (contract?.key && contract.key !== "") selectedContract = contract.key as string;

		if (customer?.key && customer.key !== "") selectedCustomer = customer.key as string;

		if (jobStatus?.key && jobStatus.key !== "") selectedJobStatus = jobStatus.key as string;

		if (!selectedStartDate || !selectedEndDate) {
			setHasErrorInDateRange(true);
			return;
		}

		setLoading(true);

		const url = await reportStore.getSoldHoursAndRevenueReport(
			selectedStartDate,
			selectedEndDate,
			selectedPool,
			selectedContract,
			selectedCustomer,
			selectedJobStatus,
			isDebug
		);

		if (url) {
			// https://stackoverflow.com/a/59352848
			const downloadLink = document.createElement("a");
			downloadLink.href = apiStore.apiRoot + url;
			downloadLink.download = apiStore.apiRoot + url;
			downloadLink.click();
		}

		setLoading(false);
	};

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={SoldHoursAndRevenueReportBreadcrumbs} />

			<Flex
				maxWidth="700px"
				padding="0.75rem 2rem"
				backgroundColor="white"
				border="1px solid grey"
				margin="1rem 0.5rem"
			>
				<ul>
					{t("reports.soldHourHelpText")
						.split("\n")
						.map((item, key) => {
							return (
								<li key={key}>
									<AppText margin="0.5rem 0">{item}</AppText>
								</li>
							);
						})}
				</ul>
			</Flex>

			<Flex flexWrap="wrap" maxWidth="900px">
				<AppRangePicker
					label={t("reports.filters.range.placeHolder")}
					placeholder={t("reports.filters.range.label")}
					value={range}
					onChange={(range) => {
						setRange(range);
						setHasErrorInDateRange(false);
					}}
					mandatory
					containerWidth={"300px"}
					containerMargin="0.5rem"
					error={hasErrorInDateRange ? t("END_DATE_START_DATE_REQUIRED_VALIDATION") : ""}
				/>
				{pools.length > 0 && (
					<Dropdown<KeyValue, KeyValue>
						value={pool}
						getValue={(o) => o}
						onChange={handlePoolChange}
						getOptionLabel={(option) => option.value}
						options={pools}
						placeholder={"reports.filters.pool.placeHolder"}
						label={"reports.filters.pool.label"}
						width="300px"
						margin="0.5rem"
					/>
				)}
				<Dropdown<KeyValue, KeyValue>
					value={contract}
					getValue={(o) => o}
					onChange={handleContractChange}
					getOptionLabel={(option) => option.value}
					options={contractOptions}
					placeholder={"reports.filters.contract.placeHolder"}
					label={"reports.filters.contract.label"}
					width="300px"
					margin="0.5rem"
				/>

				<Dropdown<KeyValue, KeyValue>
					value={customer}
					getValue={(o) => o}
					onChange={handleCustomerChange}
					getOptionLabel={(option) => option.value}
					options={customerOptions}
					placeholder={"reports.filters.customer.placeHolder"}
					label={"reports.filters.customer.label"}
					width="300px"
					margin="0.5rem"
				/>
				<Dropdown<KeyValue, KeyValue>
					value={jobStatus}
					getValue={(o) => o}
					onChange={handleJobStatusChange}
					getOptionLabel={(option) => option.value}
					options={jobStatusTypes()}
					placeholder={"reports.filters.jobStatus.placeHolder"}
					label={"reports.filters.jobStatus.label"}
					width="300px"
					margin="0.5rem"
				/>
			</Flex>

			<Flex flexWrap="wrap" maxWidth="900px">
				<DownloadButton
					width="200px"
					height="38px"
					fontSize="m"
					margin="1.5rem 0.5rem"
					onClick={onSubmit}
					loading={loading}
				>
					{t("reports.export")}
				</DownloadButton>
				{/* <HelpTooltip>
					<ul>
						<li>{t("reports.soldHourHelpText.line1")}</li>
						<li>{t("reports.soldHourHelpText.line2")}</li>
						<li>{t("reports.soldHourHelpText.line3")}</li>
						<li>{t("reports.soldHourHelpText.line4")}</li>
					</ul>
				</HelpTooltip> */}
			</Flex>
		</div>
	);
};

export default SoldHoursAndRevenueReportPage;
